import { CostRow } from './api';

/** Shared building blocks for the cost pages (Total Cost, Service Features). */

export const ENV_OPTIONS = [
  { value: '', label: 'All environments' },
  { value: 'prod', label: 'Production' },
  { value: 'dev', label: 'Development' },
  { value: 'uat', label: 'UAT' },
];

// Cost-center tag values (GCP `cost-center` tag). Add a line here when a new
// cost center is introduced; "unassigned" covers untagged + all non-GCP rows.
export const COST_CENTER_OPTIONS = [
  { value: '', label: 'All cost centers' },
  { value: 'mediasuite-core', label: 'MediaSuite Core' },
  { value: 'ad-engine', label: 'Ad Engine' },
  { value: 'ott', label: 'OTT' },
  { value: 'subgen', label: 'SubGen' },
  { value: 'administration', label: 'Administration' },
  { value: 'unassigned', label: 'Unassigned' },
];

export const PIE_COLORS = [
  '#571171', '#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6',
  '#ef4444', '#14b8a6', '#f96828', '#64748b', '#ec4899',
];

export const hkdFormat = new Intl.NumberFormat('en-HK', {
  style: 'currency',
  currency: 'HKD',
  maximumFractionDigits: 2,
});

/** The last 12 months as "YYYY-MM", newest first. */
export function lastTwelveMonths(): string[] {
  const months: string[] = [];
  const d = new Date();
  for (let i = 0; i < 12; i++) {
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return months;
}

/** Previous month of a "YYYY-MM" string. */
export function prevMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** Group label for rows without a project (e.g. AWS payer-level rows). */
export function projectKey(r: CostRow): string {
  return r.projectId ?? `${r.cloud.toUpperCase()} · (no project)`;
}

export function featureKey(r: CostRow): string {
  return r.feature ?? 'Unmapped';
}

export type MiscScope = 'project' | 'feature';

/** Identity of a row across months, for the MoM delta lookup. */
export function rowKey(r: CostRow, scope: MiscScope = 'project'): string {
  if (r.service === 'Miscellaneous') {
    return scope === 'project'
      ? `${r.accountId}|${r.projectId ?? ''}|MISC`
      : `${r.cloud}|${featureKey(r)}|MISC`;
  }
  return `${r.accountId}|${r.projectId ?? ''}|${r.skuId ?? r.sku}|${r.env}`;
}

/** SKUs under this many USD are bundled into one "Miscellaneous" row per group. */
export const MISC_USD_THRESHOLD = 1;
const USD_TO_HKD_APPROX = 7.8; // only for thresholding non-USD rows, not display

export function isSmall(r: CostRow): boolean {
  const usd = r.nativeCurrency === 'USD' ? r.nativeCost : r.hkdCost / USD_TO_HKD_APPROX;
  // abs(): keep large refunds/adjustments visible instead of hiding them in Misc
  return Math.abs(usd) < MISC_USD_THRESHOLD;
}

/**
 * Replace each group's sub-threshold SKUs with one aggregated Miscellaneous
 * row. Grouped per project (Total Cost page) or per cloud+feature
 * (Service Features page).
 */
export function withMisc(rows: CostRow[], scope: MiscScope = 'project'): CostRow[] {
  const big: CostRow[] = [];
  const smallByGroup = new Map<string, CostRow[]>();
  for (const r of rows) {
    if (!isSmall(r)) {
      big.push(r);
      continue;
    }
    const key = scope === 'project' ? projectKey(r) : `${r.cloud}|${featureKey(r)}`;
    const list = smallByGroup.get(key) ?? [];
    list.push(r);
    smallByGroup.set(key, list);
  }
  const miscRows: CostRow[] = [...smallByGroup.values()].map((rs) => {
    const currencies = new Set(rs.map((r) => r.nativeCurrency));
    const envs = new Set(rs.map((r) => r.env));
    return {
      cloud: rs[0].cloud,
      accountId: rs[0].accountId,
      month: rs[0].month,
      env: envs.size === 1 ? rs[0].env : 'mixed',
      service: 'Miscellaneous',
      sku: `${rs.length} SKU${rs.length === 1 ? '' : 's'} under US$${MISC_USD_THRESHOLD}`,
      projectId: scope === 'project' ? rs[0].projectId : undefined,
      feature: rs[0].feature,
      nativeCost: rs.reduce((s, r) => s + r.nativeCost, 0),
      nativeCurrency: currencies.size === 1 ? rs[0].nativeCurrency : 'mixed',
      hkdCost: rs.reduce((s, r) => s + r.hkdCost, 0),
    };
  });
  return [...big, ...miscRows].sort((a, b) => b.hkdCost - a.hkdCost);
}

/**
 * Rows grouped by service, sorted by subtotal desc, with the Miscellaneous
 * bundle always last.
 */
export function groupByService(
  rows: CostRow[]
): { service: string; rows: CostRow[]; hkdTotal: number }[] {
  const groups = new Map<string, { service: string; rows: CostRow[]; hkdTotal: number }>();
  for (const r of rows) {
    const g = groups.get(r.service) ?? { service: r.service, rows: [], hkdTotal: 0 };
    g.rows.push(r);
    g.hkdTotal += r.hkdCost;
    groups.set(r.service, g);
  }
  return [...groups.values()].sort((a, b) => {
    if (a.service === 'Miscellaneous') return 1;
    if (b.service === 'Miscellaneous') return -1;
    return b.hkdTotal - a.hkdTotal;
  });
}

export function CloudBadge({ cloud }: { cloud: string }) {
  const styles: Record<string, string> = {
    aws: 'bg-orange-100 text-orange-700',
    gcp: 'bg-blue-100 text-blue-700',
    alicloud: 'bg-amber-100 text-amber-700',
    nextlink: 'bg-purple-100 text-purple-700',
    byteplus: 'bg-cyan-100 text-cyan-700',
  };
  return (
    <span
      className={
        'inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide ' +
        (styles[cloud] ?? 'bg-slate-100 text-slate-600')
      }
    >
      {cloud.toUpperCase()}
    </span>
  );
}

export function DeltaBadge({ current, prev }: { current: number; prev: number | undefined }) {
  if (prev === undefined) {
    return <span className="text-xs text-sky-600">new</span>;
  }
  if (prev <= 0) {
    return <span className="text-xs text-slate-300">—</span>;
  }
  const pct = ((current - prev) / prev) * 100;
  if (Math.abs(pct) < 0.05) {
    return <span className="text-xs text-slate-400">0%</span>;
  }
  // Cost going up is bad → red; going down is good → green
  return (
    <span className={'text-xs font-mono ' + (pct > 0 ? 'text-red-600' : 'text-emerald-600')}>
      {pct > 0 ? '▲' : '▼'} {Math.abs(pct) >= 100 ? Math.round(Math.abs(pct)) : Math.abs(pct).toFixed(1)}%
    </span>
  );
}

export function SkuRow({ row: r, prevHkd }: { row: CostRow; prevHkd: number | undefined }) {
  const isMisc = r.service === 'Miscellaneous';
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50">
      <td className="px-4 py-2.5">
        <CloudBadge cloud={r.cloud} />
      </td>
      <td className={'px-4 py-2.5 whitespace-nowrap' + (isMisc ? ' italic text-slate-500' : '')}>
        {r.service}
      </td>
      <td className={'px-4 py-2.5' + (isMisc ? ' italic text-slate-500' : '')}>{r.sku}</td>
      <td className="px-4 py-2.5 font-mono text-xs">{r.projectId ?? '—'}</td>
      <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">{r.accountId}</td>
      <td className="px-4 py-2.5">
        <span
          className={
            'inline-block px-2 py-0.5 rounded-full text-xs ' +
            (r.env === 'prod'
              ? 'bg-emerald-100 text-emerald-700'
              : r.env === 'dev'
                ? 'bg-sky-100 text-sky-700'
                : 'bg-slate-100 text-slate-500')
          }
        >
          {r.env}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right font-mono whitespace-nowrap">
        {r.nativeCost.toFixed(2)} {r.nativeCurrency}
      </td>
      <td className="px-4 py-2.5 text-right font-mono whitespace-nowrap">
        {hkdFormat.format(r.hkdCost)}
      </td>
      <td className="px-4 py-2.5 text-right whitespace-nowrap">
        <DeltaBadge current={r.hkdCost} prev={prevHkd} />
      </td>
    </tr>
  );
}
